import { API, authFetch } from "@/App";
import { toast } from "sonner";

const ORDER_SYNC_CHANNEL = "orderflow_order_sync";

// Detect payment provider from email content
function detectPaymentProvider(data, emailText) {
  const result = { ...data };
  const paymentMethodKeys = ['payment_method', 'PAYMENT METHOD'];

  let currentValue = null;
  for (const k of paymentMethodKeys) {
    if (result[k] && String(result[k]).trim() && !['', '-', 'None', 'null'].includes(String(result[k]).trim())) {
      currentValue = result[k];
      break;
    }
  }
  if (currentValue) return result;

  const emailLower = emailText.toLowerCase();
  const providers = [
    [/stripe/i, 'STRIPE'],
    [/pi_[a-zA-Z0-9]+/i, 'STRIPE'],
    [/paypal/i, 'PAYPAL'],
    [/virement\s*(bancaire|sepa)?/i, 'VIREMENT'],
    [/transfert\s*bancaire/i, 'VIREMENT'],
    [/bank\s*transfer/i, 'VIREMENT'],
    [/chèque|cheque/i, 'CHÈQUE'],
    [/carte\s*(bancaire|de\s*crédit|bleue)/i, 'CB'],
    [/credit\s*card/i, 'CB'],
    [/visa|mastercard|amex/i, 'CB'],
    [/espèces|cash/i, 'ESPÈCES'],
    [/prélèvement/i, 'PRÉLÈVEMENT'],
  ];

  for (const [pattern, provider] of providers) {
    if (pattern.test(emailLower)) {
      result['payment_method'] = provider;
      break;
    }
  }
  return result;
}

// Apply automatic payment rules
function applyPaymentRules(data) {
  const result = { ...data };
  const toKey = (colName) => colName.toLowerCase().replace(/\s+/g, '_').replace(/'/g, '').replace(/-/g, '_').replace(/\./g, '_');

  const paymentMethodKeys = ['PAYMENT METHOD', 'payment_method', toKey('PAYMENT METHOD')];
  const datePaymentKeys = ['DATE OF PAYMENT', 'date_of_payment', toKey('DATE OF PAYMENT')];
  const dateConfirmKeys = ['DATE OF PAYMENT CONFIRM.', 'date_of_payment_confirm_', toKey('DATE OF PAYMENT CONFIRM.')];
  const dateOrderKeys = ['DATE OF ORDER TO SUPPLIER', 'date_of_order_to_supplier',
    'DATE OF ORDER', 'date_of_order', toKey('DATE OF ORDER'), toKey('DATE OF ORDER TO SUPPLIER')];
  const statutKeys = ['STATUT REGLEMENT', 'statut_reglement', toKey('STATUT REGLEMENT')];

  const getValue = (keys) => {
    for (const k of keys) {
      if (result[k] && String(result[k]).trim() && !['', '-', 'None', 'null'].includes(String(result[k]).trim())) {
        return [result[k], k];
      }
    }
    return [null, null];
  };

  const setValue = (keys, value) => {
    for (const k of keys) {
      if (k in result) { result[k] = value; return k; }
    }
    const displayKey = keys[0];
    const normalizedKey = toKey(displayKey);
    result[displayKey] = value;
    if (normalizedKey !== displayKey.toLowerCase()) {
      result[normalizedKey] = value;
    }
    return displayKey;
  };

  let [paymentMethodValue] = getValue(paymentMethodKeys);
  let [datePaymentValue] = getValue(datePaymentKeys);
  let [dateConfirmValue] = getValue(dateConfirmKeys);
  let [dateOrderValue] = getValue(dateOrderKeys);
  let [statutValue] = getValue(statutKeys);

  if (paymentMethodValue && !datePaymentValue && dateOrderValue) {
    setValue(datePaymentKeys, dateOrderValue);
    datePaymentValue = dateOrderValue;
  }
  if (datePaymentValue && !dateConfirmValue) {
    setValue(dateConfirmKeys, datePaymentValue);
  }
  if (paymentMethodValue && !statutValue) {
    setValue(statutKeys, "PAYÉ");
  }

  return result;
}

export function useExtraction({
  emailContent, setEmailContent, extractedData, setExtractedData, editedData, setEditedData,
  currentOrderId, setCurrentOrderId, selectedProfileId, validationModal, setValidationModal,
  setIsSavingValidation, detectedCompany, setDetectedCompany, collectedEmails, setCollectedEmails,
  setExtractionInfo, setIsExtracting, conflictsModal, setConflictsModal,
  enrichEmailContent, setEnrichEmailContent, setIsEnrichModalOpen, setIsEnriching,
  navigate, onOrderCreated,
}) {
  // Extract order from email
  const handleExtract = async () => {
    if (!emailContent.trim()) {
      toast.error("Veuillez coller le contenu de l'email");
      return;
    }

    // If existing order, use enrich endpoint
    if (currentOrderId && extractedData && Object.keys(extractedData).length > 0) {
      setIsExtracting(true);
      try {
        const response = await authFetch(`${API}/orders/${currentOrderId}/enrich`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email_content: emailContent,
            company_profile_id: selectedProfileId && selectedProfileId !== "auto" ? selectedProfileId : null,
          }),
        });
        if (response.ok) {
          const data = await response.json();
          let enrichedData = detectPaymentProvider(data.extracted_data, emailContent);
          enrichedData = applyPaymentRules(enrichedData);
          setValidationModal({
            open: true, mode: "enrichissement",
            extractedData: enrichedData, existingData: extractedData || {},
            emailContent: emailContent,
          });
        } else {
          const error = await response.json();
          toast.error(error.detail || "Erreur lors de l'enrichissement");
        }
      } catch (error) {
        console.error("Enrichment failed:", error);
        toast.error("Erreur lors de l'enrichissement");
      } finally {
        setIsExtracting(false);
      }
      return;
    }

    setIsExtracting(true);

    try {
      const response = await authFetch(`${API}/orders/extract`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_content: emailContent,
          company_profile_id: selectedProfileId && selectedProfileId !== "auto" ? selectedProfileId : null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        let newExtracted = data.extracted_data;
        if (data.extraction_info) setExtractionInfo(data.extraction_info);
        newExtracted = detectPaymentProvider(newExtracted, emailContent);
        newExtracted = applyPaymentRules(newExtracted);
        if (data.detected_company) setDetectedCompany(data.detected_company);

        if (currentOrderId && extractedData && Object.keys(extractedData).length > 0) {
          setValidationModal({
            open: true, mode: "enrichissement",
            extractedData: newExtracted, existingData: extractedData,
            emailContent: emailContent,
          });
        } else {
          setValidationModal({
            open: true, mode: "extraction",
            extractedData: newExtracted, existingData: {},
            emailContent: emailContent,
            potentialDuplicates: data.potential_duplicates || [],
            invoiceTotals: data.invoice_totals || null,
          });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'extraction");
      }
    } catch (error) {
      console.error("Extraction failed:", error);
      toast.error("Erreur lors de l'extraction");
    } finally {
      setIsExtracting(false);
    }
  };

  // Handle save from validation modal
  const handleValidationSave = async (validatedData, existingOrderId) => {
    setIsSavingValidation(true);
    try {
      const mode = validationModal.mode;
      const emailCont = validationModal.emailContent;
      const dataToSave = applyPaymentRules(validatedData);

      if (mode === "extraction") {
        const createResponse = await authFetch(`${API}/orders/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            extracted_data: dataToSave, raw_email: emailCont,
            emails: [{ type: "commande", content: emailCont, date: new Date().toISOString() }],
            detected_company: detectedCompany,
          }),
        });
        if (createResponse.ok) {
          const createData = await createResponse.json();
          const newOrderId = createData.order_id;
          setCurrentOrderId(newOrderId);
          setExtractedData(dataToSave);
          setEditedData({ ...dataToSave });
          setCollectedEmails([{ type: "commande", content: emailCont, date: new Date().toISOString() }]);
          const channel = new BroadcastChannel(ORDER_SYNC_CHANNEL);
          channel.postMessage({ type: 'NEW_ORDER', order: { order_id: newOrderId, extracted_data: dataToSave, created_at: new Date().toISOString() } });
          channel.close();
          toast.success("Commande créée avec succès !");
          onOrderCreated?.(newOrderId, dataToSave);
          setEmailContent("");
        } else {
          const error = await createResponse.json();
          toast.error(error.detail || "Erreur lors de la création");
        }
      } else {
        const orderId = existingOrderId || currentOrderId;
        if (!orderId) { toast.error("Erreur: Aucune commande à mettre à jour"); return; }

        const updateResponse = await authFetch(`${API}/orders/${orderId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order_id: orderId, extracted_data: dataToSave }),
        });
        if (updateResponse.ok) {
          await authFetch(`${API}/orders/${orderId}/emails`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ type: "reglement", content: emailCont, date: new Date().toISOString() }),
          });
          setExtractedData(dataToSave);
          setEditedData({ ...dataToSave });
          setCollectedEmails(prev => [...prev, { type: "reglement", content: emailCont, date: new Date().toISOString() }]);
          const channel = new BroadcastChannel(ORDER_SYNC_CHANNEL);
          channel.postMessage({ type: 'ORDER_UPDATED', order: { order_id: orderId, extracted_data: validatedData, updated_at: new Date().toISOString() } });
          channel.close();
          toast.success("Commande mise à jour avec succès !");
          setEmailContent("");
        } else {
          const error = await updateResponse.json();
          toast.error(error.detail || "Erreur lors de la mise à jour");
        }
      }
      setValidationModal({ open: false, mode: "extraction", extractedData: {}, existingData: {}, emailContent: "" });
    } catch (error) {
      console.error("Save failed:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setIsSavingValidation(false);
    }
  };

  // Enrich from main zone
  const handleEnrichFromMainZone = async () => {
    if (!currentOrderId) { toast.error("Veuillez d'abord enregistrer la commande"); return; }
    if (!emailContent.trim()) { toast.error("Veuillez coller le contenu de l'email"); return; }
    setIsEnriching(true);
    try {
      const response = await authFetch(`${API}/orders/${currentOrderId}/enrich`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_content: emailContent,
          company_profile_id: selectedProfileId && selectedProfileId !== "auto" ? selectedProfileId : null,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        let enrichedData = detectPaymentProvider(data.extracted_data, emailContent);
        enrichedData = applyPaymentRules(enrichedData);
        setValidationModal({
          open: true, mode: "enrichissement",
          extractedData: enrichedData, existingData: extractedData || {},
          emailContent: emailContent,
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'enrichissement");
      }
    } catch (error) {
      console.error("Enrichment failed:", error);
      toast.error("Erreur lors de l'enrichissement");
    } finally {
      setIsEnriching(false);
    }
  };

  // Enrich from modal
  const handleEnrich = async () => {
    if (!currentOrderId) { toast.error("Aucune commande à compléter"); return; }
    if (!enrichEmailContent.trim()) { toast.error("Veuillez coller le contenu de l'email"); return; }
    setIsEnriching(true);
    try {
      const response = await authFetch(`${API}/orders/${currentOrderId}/enrich`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_content: enrichEmailContent,
          company_profile_id: selectedProfileId && selectedProfileId !== "auto" ? selectedProfileId : null,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        let enrichedData = detectPaymentProvider(data.extracted_data, enrichEmailContent);
        enrichedData = applyPaymentRules(enrichedData);
        setIsEnrichModalOpen(false);
        setEnrichEmailContent("");
        setValidationModal({
          open: true, mode: "enrichissement",
          extractedData: enrichedData, existingData: extractedData || {},
          emailContent: enrichEmailContent,
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'enrichissement");
      }
    } catch (error) {
      console.error("Enrichment failed:", error);
      toast.error("Erreur lors de l'enrichissement");
    } finally {
      setIsEnriching(false);
    }
  };

  // Resolve a conflict
  const resolveConflict = (fieldName, value) => {
    setConflictsModal(prev => ({ ...prev, resolved: { ...prev.resolved, [fieldName]: value } }));
    setEditedData(prev => ({ ...prev, [fieldName]: value }));
  };

  // Apply all resolved conflicts
  const applyResolvedConflicts = () => {
    const allResolved = Object.keys(conflictsModal.conflicts).every(key => conflictsModal.resolved[key] !== undefined);
    if (!allResolved) { toast.error("Veuillez résoudre tous les conflits"); return; }
    let newEditedData = { ...editedData };
    for (const [key, value] of Object.entries(conflictsModal.resolved)) {
      newEditedData[key] = value;
    }
    newEditedData = applyPaymentRules(newEditedData);
    setEditedData(newEditedData);
    setExtractedData(newEditedData);
    setConflictsModal({ open: false, conflicts: {}, resolved: {} });
    toast.success("Conflits résolus !");
  };

  return {
    handleExtract, handleValidationSave,
    handleEnrichFromMainZone, handleEnrich,
    resolveConflict, applyResolvedConflicts,
  };
}
